import { Bound } from "lib/@components/binding/Bound"
import { useBoundValue } from "lib/@components/binding/use-bound-value"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { DialogBox } from "lib/@components/dialogBox"
import { Divider, Grid2, List, Stack, Typography } from "@mui/material"
import { Repeat } from "lib/@components/repeat"
import { assetDefinition } from "routes/asset-register/controller/asset-definition"
import { BoundTextField } from "lib/@components/binding/bound-components"
import { DatePicker } from "lib/@components/datePicker"
import { optimiseVisits } from "routes/plan/plugins/weekly-planner/plugins/planner-schedule-list/optimise-visits"
import { useMemo, useState } from "react"
import { convertPeriodLabelToFrequency } from "library/convert-period-label-to-frequency.mjs"
import { periodIntervalHoursFromTask } from "library/period-interval-hours-from-task.mjs"
import { dayLength } from "routes/plan/server/constants.mjs"

export function WeekPlannerMoveDialog({ plan, ...props }) {
    return (
        <Bound target={plan}>
            <InnerWeekPlannerMoveDialogVisits {...props} />
        </Bound>
    )
}

function InnerWeekPlannerMoveDialogVisits({ scheduleId, scheduleItem }) {
    const [treeGroup, id] = scheduleId.split("!")

    const [group] = treeGroup.split("~")
    const sid = `${group}!${id}`
    const [original, setConfigurations] = useBoundValue("configurations", {})
    const update = useMemo(() => structuredClone(original), [original])

    const { days } = useBoundContext()

    const allTasks = days
        .map((d) => d.scheduleIndex[sid])
        .filter(Boolean)
        .flatMap((s) => s.tasks)

    const allAssets = allTasks.unique("assetIndex")

    const allVisits = optimiseVisits(
        days
            .map((d) => d.scheduleIndex[sid])
            .filter(Boolean)
            .flatMap((s) => s.visits)
            .unique("label")
    )

    return (
        <Bound
            scheduleItem={scheduleItem}
            allAssets={allAssets}
            allVisits={allVisits}
            configurations={update}
            allTasks={allTasks}
        >
            <DialogBox title={scheduleItem.fullPath} beforeOk={store} value={true}>
                <List>
                    <Stack spacing={2}>
                        <Repeat list={allAssets} item={<AssetPlanner allVisits={allVisits} />} />
                    </Stack>
                </List>
            </DialogBox>
        </Bound>
    )

    async function store() {
        setConfigurations(update)
    }
}

function AssetPlanner({ item, allVisits, last }) {
    const { allTasks } = useBoundContext()
    return (
        <Bound
            allTasks={allTasks.filter((t) => t.assetIndex === item.assetIndex)}
            assetId={item.assetId}
            assetIndex={item.assetIndex}
        >
            <AssetView assetId={item.assetId} />
            <Repeat list={allVisits} item={<AssetVisit />} />
            {!last && <Divider />}
        </Bound>
    )
}

function AssetVisit({ item }) {
    const { allTasks, plan, configurations, scheduleItem, assetIndex } = useBoundContext()
    const intervalInHours = periodIntervalHoursFromTask({ frequency: convertPeriodLabelToFrequency(item.label) })
    const task = allTasks.find((t) => t.intervalInHours === intervalInHours)
    const startId = task ? task.id.split(".").slice(0, -1).join(".") + ".0" : "Unlikely"
    const config = configurations[startId] ?? {}

    const [date, setDate] = useState(+new Date(plan.date) + (config.startOffset ?? 0) * dayLength)
    useMemo(() => {
        let dateToUse = new Date(date)
        while (dateToUse < plan.date) {
            dateToUse.addYears(1)
        }

        for (const task of allTasks) {
            if (task.assetIndex !== assetIndex) continue
            if (task.intervalInHours <= intervalInHours) {
                if (intervalInHours % task.intervalInHours === 0) {
                    let scanDate = new Date(+dateToUse)
                    let lastDate = new Date(+scanDate)
                    const targetDate = new Date(plan.date)

                    while (scanDate > targetDate) {
                        lastDate = new Date(+scanDate)
                        switch (task.task.frequency.period) {
                            case "Day":
                                scanDate = scanDate.addDays(-task.task.frequency.interval ?? 1)
                                break
                            case "Week":
                                scanDate = scanDate.addWeeks(-task.task.frequency.interval ?? 1)
                                break
                            case "Month":
                                scanDate = scanDate.addMonths(-task.task.frequency.interval ?? 1)
                                break
                            case "Year":
                                scanDate = scanDate.addYears(-task.task.frequency.interval ?? 1)
                                break
                        }
                    }

                    const startId = task.id.split(".").slice(0, -2).join(".") + `.${assetIndex}.0`
                    const config = (configurations[startId] ??= {})
                    Object.assign(config, {
                        scheduleId: scheduleItem.scheduleId,
                        period: 0,
                        assetIndex,
                        taskId: task.task.id,
                        startOffset: Math.floor((lastDate - new Date(plan.date)) / dayLength),
                    })
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date])

    return (
        <DatePicker
            textFieldProps={{ size: "small" }}
            value={date}
            onChange={setDate}
            label={item.label + " first date"}
        />
    )
}

function AssetView({ assetId }) {
    const asset = assetDefinition.useResults(assetId)
    if (!asset) {
        return <Typography variant="body2">{assetId}</Typography>
    } else {
        return (
            <Bound target={asset}>
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 6, md: 4 }}>
                        <BoundTextField size="small" field="tag" inputProps={{ readOnly: true }} />
                    </Grid2>
                    <Grid2 size={{ xs: 6, md: 4 }}>
                        <Typography variant="body2">
                            {[
                                `${asset.name ? asset.name : ""} ${asset.system ? `part of the "${asset.system}" system` : ""}`,
                                `${asset.manufacturer ? `made by ${asset.manufacturer}` : ""} ${asset.model ? `with model "${asset.model}"` : ""}`,
                            ]
                                .filter((c) => c.length > 1)
                                .join(", ")}
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 6, md: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                            {[`${asset.facility ? `Located in ${asset.facility}` : ""}`, asset.floor, asset.space]
                                .filter((c) => c?.length > 1)
                                .join(", ")}
                        </Typography>
                    </Grid2>
                </Grid2>
            </Bound>
        )
    }
}
